import React, { useState } from 'react';
import { theme, fontStack, bodyFont } from '../theme';
import { FadeIn, Divider, CrossIcon } from '../components/shared';
import churchConfig from '../churchConfig';
import { useChurchData, useChurchInfo } from '../data/ChurchDataProvider';

export default function JoinUsPage() {
  const { data } = useChurchData();
  const info = useChurchInfo();
  const addr = info.address;
  const form = data.formConfig || {};
  const fields = (form.fields || []).filter(f => f.enabled);

  // Build initial form state from fields
  const initFormData = () => {
    const obj = {};
    fields.forEach(f => { obj[f.id] = ""; });
    return obj;
  };
  const [formData, setFormData] = useState(initFormData);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSubmitting(true);
    setSubmitError("");

    // Build readable payload
    const payload = {};
    fields.forEach(f => { payload[f.label] = formData[f.id] || "(not provided)"; });

    try {
      const service = form.emailService || "none";

      if (service === "formspree" && form.formspreeId) {
        const res = await fetch(`https://formspree.io/f/${form.formspreeId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Accept": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Formspree error");

      } else if (service === "web3forms" && form.web3formsKey) {
        const res = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ access_key: form.web3formsKey, subject: "New Contact Form - NPBC Website", ...payload }),
        });
        if (!res.ok) throw new Error("Web3Forms error");

      } else if (service === "emailjs" && form.emailjsPublicKey) {
        const res = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            service_id: form.emailjsServiceId,
            template_id: form.emailjsTemplateId,
            user_id: form.emailjsPublicKey,
            template_params: { ...payload, message: fields.map(f => `${f.label}: ${formData[f.id] || "(not provided)"}`).join("\n") },
          }),
        });
        if (res.status !== 200) throw new Error("EmailJS error");

      } else if (service === "mailto" && form.mailtoAddress) {
        const subject = encodeURIComponent("New Contact Form Submission - NPBC Website");
        const body = encodeURIComponent(fields.map(f => `${f.label}: ${formData[f.id] || "(not provided)"}`).join("\n"));
        window.location.href = `mailto:${form.mailtoAddress}?subject=${subject}&body=${body}`;

      } else {
        // No service configured — just show success (dev/demo mode)
      }

      setSubmitted(true);
    } catch (err) {
      setSubmitError("Something went wrong sending your message. Please try again or call the church directly.");
      console.error("Form submission error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = { width: "100%", padding: "14px 18px", background: theme.bgCard, border: `1px solid ${theme.border}`, color: theme.cream, fontFamily: bodyFont, fontSize: 15, outline: "none", transition: "border-color 0.3s", boxSizing: "border-box" };
  const labelStyle = { fontFamily: bodyFont, fontSize: 11, fontWeight: 600, color: theme.gold, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 8, display: "block" };

  // Group half-width fields into pairs
  const renderFields = () => {
    const elements = [];
    let i = 0;
    while (i < fields.length) {
      const f = fields[i];
      // Check if this and next field are both halfWidth
      if (f.halfWidth && i + 1 < fields.length && fields[i + 1].halfWidth && fields[i + 1].enabled) {
        const f2 = fields[i + 1];
        elements.push(
          <div key={f.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {renderField(f)}
            {renderField(f2)}
          </div>
        );
        i += 2;
      } else {
        elements.push(<div key={f.id}>{renderField(f)}</div>);
        i++;
      }
    }
    return elements;
  };

  const renderField = (f) => {
    const val = formData[f.id] || "";
    const onChange = e => setFormData({ ...formData, [f.id]: e.target.value });
    const focusHandlers = {
      onFocus: e => e.target.style.borderColor = theme.gold,
      onBlur: e => e.target.style.borderColor = theme.border,
    };

    if (f.type === "select") {
      const opts = (f.options || "").split(",").map(o => o.trim()).filter(Boolean);
      return (
        <div>
          <label style={labelStyle}>{f.label}</label>
          <select style={{ ...inputStyle, cursor: "pointer", appearance: "none" }} value={val} onChange={onChange} required={f.required} {...focusHandlers}>
            <option value="" style={{ background: theme.bgCard }}>{f.placeholder || "Select..."}</option>
            {opts.map(o => <option key={o} value={o.toLowerCase().replace(/\s+/g, '-')} style={{ background: theme.bgCard }}>{o}</option>)}
          </select>
        </div>
      );
    }
    if (f.type === "textarea") {
      return (
        <div>
          <label style={labelStyle}>{f.label}</label>
          <textarea style={{ ...inputStyle, minHeight: 120, resize: "vertical" }} placeholder={f.placeholder} value={val} onChange={onChange} required={f.required} {...focusHandlers} />
        </div>
      );
    }
    return (
      <div>
        <label style={labelStyle}>{f.label}</label>
        <input type={f.type || "text"} style={inputStyle} placeholder={f.placeholder} value={val} onChange={onChange} required={f.required} {...focusHandlers} />
      </div>
    );
  };

  return (
    <div style={{ paddingTop: 100 }}>
      <section style={{ padding: "80px 24px 40px", textAlign: "center" }}>
        <FadeIn>
          <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 500, color: theme.gold, letterSpacing: "0.3em", textTransform: "uppercase", marginBottom: 16 }}>Connect With Us</div>
          <h1 style={{ fontFamily: fontStack, fontSize: "clamp(36px, 6vw, 64px)", fontWeight: 300, color: theme.cream, lineHeight: 1.1 }}>
            Join <span style={{ fontStyle: "italic", fontWeight: 600, color: theme.gold }}>Our Family</span>
          </h1>
          <Divider width={60} />
          <p style={{ fontFamily: bodyFont, fontSize: 16, color: theme.textMuted, maxWidth: 520, margin: "16px auto 0", lineHeight: 1.7 }}>
            We'd love to hear from you. Whether you're visiting for the first time or looking to become a member, reach out and let us know how we can serve you.
          </p>
        </FadeIn>
      </section>

      <section style={{ padding: "0 24px 100px" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 40 }}>
          {/* Form */}
          <FadeIn>
            <div style={{ padding: 40, background: theme.bgCard, border: `1px solid ${theme.border}` }}>
              {submitted ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                  <div style={{ fontSize: 48, marginBottom: 20 }}>✨</div>
                  <h3 style={{ fontFamily: fontStack, fontSize: 28, fontWeight: 500, color: theme.cream, marginBottom: 12 }}>Thank You!</h3>
                  <p style={{ fontFamily: bodyFont, fontSize: 15, color: theme.textMuted, lineHeight: 1.7, marginBottom: 24 }}>{form.successMessage || "We've received your message and will be in touch soon."}</p>
                  <button onClick={() => { setSubmitted(false); setFormData(initFormData()); }}
                    style={{ padding: "12px 28px", background: "transparent", border: `1px solid ${theme.gold}`, color: theme.gold, fontFamily: bodyFont, fontSize: 12, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", cursor: "pointer", transition: "all 0.3s" }}
                    onMouseEnter={e => { e.target.style.background = theme.gold; e.target.style.color = theme.bg; }}
                    onMouseLeave={e => { e.target.style.background = "transparent"; e.target.style.color = theme.gold; }}
                  >Send Another Message</button>
                </div>
              ) : (
                <>
                  <h3 style={{ fontFamily: fontStack, fontSize: 26, fontWeight: 500, color: theme.cream, marginBottom: 8 }}>{form.heading || "Get In Touch"}</h3>
                  <p style={{ fontFamily: bodyFont, fontSize: 14, color: theme.textMuted, marginBottom: 32, lineHeight: 1.7 }}>{form.subheading || ""}</p>
                  <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                    {renderFields()}
                    {submitError && (
                      <div style={{ padding: 12, background: "rgba(231,76,60,0.1)", border: "1px solid rgba(231,76,60,0.3)", fontFamily: bodyFont, fontSize: 13, color: "#e74c3c", lineHeight: 1.5 }}>
                        {submitError}
                      </div>
                    )}
                    <button type="submit" className="btn-primary" style={{ marginTop: 8, opacity: submitting ? 0.6 : 1, pointerEvents: submitting ? "none" : "auto" }}>
                      {submitting ? "Sending..." : (form.submitLabel || "Send Message")}
                    </button>
                    {(!form.emailService || form.emailService === "none") && (
                      <p style={{ fontFamily: bodyFont, fontSize: 11, color: theme.textMuted, textAlign: "center", marginTop: -8 }}>
                        Form delivery not yet configured. Contact the church at <strong style={{ color: theme.cream }}>{info.phone}</strong>
                      </p>
                    )}
                  </form>
                </>
              )}
            </div>
          </FadeIn>

          {/* Info Sidebar */}
          <FadeIn delay={0.2}>
            <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
              {/* Service Times */}
              <div style={{ padding: 32, background: theme.bgCard, border: `1px solid ${theme.border}` }}>
                <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, marginBottom: 20 }}>Service Times</h3>
                {data.services.map((s, i) => (
                  <div key={s.id || i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: i < data.services.length - 1 ? `1px solid ${theme.border}` : "none" }}>
                    <span style={{ fontFamily: bodyFont, fontSize: 13, color: theme.textMuted }}>{s.name}</span>
                    <span style={{ fontFamily: fontStack, fontSize: 16, fontWeight: 500, color: theme.gold }}>{s.time}</span>
                  </div>
                ))}
              </div>

              {/* Location */}
              <div style={{ padding: 32, background: theme.bgCard, border: `1px solid ${theme.border}` }}>
                <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, marginBottom: 20 }}>Find Us</h3>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 600, color: theme.gold, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 }}>Address</div>
                  <p style={{ fontFamily: bodyFont, fontSize: 15, color: theme.text, lineHeight: 1.6 }}>
                    New Prospect Baptist Church<br />{addr.street}<br />{addr.city}, {addr.state} {addr.zip}
                  </p>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 600, color: theme.gold, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 }}>Phone</div>
                  <p style={{ fontFamily: bodyFont, fontSize: 15, color: theme.text }}>{info.phone}</p>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 600, color: theme.gold, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 }}>Website</div>
                  <p style={{ fontFamily: bodyFont, fontSize: 15, color: theme.text }}>{info.website}</p>
                </div>
                <div>
                  <div style={{ fontFamily: bodyFont, fontSize: 11, fontWeight: 600, color: theme.gold, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: 6 }}>E-Giving</div>
                  <p style={{ fontFamily: bodyFont, fontSize: 15, color: theme.text }}>{info.eGiving}</p>
                </div>
              </div>

              {/* What to Expect */}
              <div style={{ padding: 32, background: `linear-gradient(135deg, rgba(200,164,86,0.08) 0%, ${theme.bgCard} 100%)`, border: `1px solid ${theme.borderHover}` }}>
                <h3 style={{ fontFamily: fontStack, fontSize: 22, fontWeight: 500, color: theme.cream, marginBottom: 16 }}>What to Expect</h3>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {[
                    "Come as you are — all are welcome",
                    "Sunday Fellowship starts at 9:00 AM with coffee and conversation",
                    "Uplifting worship with hymns and contemporary praise",
                    "Nursery provided for every service (0-36 months)",
                    "Children's Church available during morning worship (ages 3 - Grade 6)",
                    "Pastor Trey delivers Biblical, life-applicable messages",
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                      <CrossIcon size={14} color={theme.gold} />
                      <span style={{ fontFamily: bodyFont, fontSize: 14, color: theme.textMuted, lineHeight: 1.6 }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
